import React, { useState, useEffect } from 'react'
import {
  Card,
  Layout,
  DataTable,
  TextField,
  Select,
  Badge,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  Spinner,
  EmptyState,
  Icon,
  InlineStack,
  Button,
  Thumbnail,
} from '@shopify/polaris'
import {
  SearchIcon,
  ProductIcon,
  ViewIcon,
  CartIcon,
  CashDollarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@shopify/polaris-icons'

function ProductAnalytics({ durationFilter }) {
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [products, setProducts] = useState([])

  // Mock data for demonstration
  useEffect(() => {
    setLoading(true)
    // Simulate API call
    setTimeout(() => {
      setProducts([
        {
          id: '1',
          title: 'Sample Product 1',
          vendor: 'Vendor A',
          status: 'ACTIVE',
          views: 1250,
          addToCart: 85,
          purchases: 32,
          revenue: 2560.00,
          conversionRate: 2.56,
          image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png'
        },
        {
          id: '2',
          title: 'Sample Product 2',
          vendor: 'Vendor B',
          status: 'ACTIVE',
          views: 890,
          addToCart: 62,
          purchases: 18,
          revenue: 1440.00,
          conversionRate: 2.02,
          image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png'
        },
      ])
      setLoading(false)
    }, 1000)
  }, [durationFilter])

  const MetricCard = ({ title, value, icon, trend }) => (
    <Card>
      <Box padding="400">
        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text variant="bodyMd" tone="subdued">{title}</Text>
            <Icon source={icon} tone="base" />
          </InlineStack>
          <Text variant="heading2xl">{value}</Text>
          {trend && (
            <InlineStack gap="100">
              <Icon
                source={trend > 0 ? ChevronUpIcon : ChevronDownIcon}
                tone={trend > 0 ? 'success' : 'critical'}
              />
              <Text variant="bodySm" tone={trend > 0 ? 'success' : 'critical'}>
                {Math.abs(trend)}%
              </Text>
            </InlineStack>
          )}
        </BlockStack>
      </Box>
    </Card>
  )

  const rows = products
    .filter(p => p.title.toLowerCase().includes(searchValue.toLowerCase()))
    .map(product => [
      <InlineStack gap="300">
        <Thumbnail source={product.image} alt={product.title} size="small" />
        <BlockStack gap="100">
          <Text variant="bodyMd" fontWeight="semibold">{product.title}</Text>
          <Text variant="bodySm" tone="subdued">{product.vendor}</Text>
        </BlockStack>
      </InlineStack>,
      <Badge tone="success">{product.status}</Badge>,
      product.views.toLocaleString(),
      product.addToCart.toLocaleString(),
      product.purchases.toLocaleString(),
      `$${product.revenue.toFixed(2)}`,
      `${product.conversionRate}%`,
    ])

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="800">
              <BlockStack gap="400" align="center">
                <Spinner size="large" />
                <Text>Loading analytics...</Text>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    )
  }

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          <Text variant="headingLg">Performance Overview</Text>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <MetricCard
              title="Total Views"
              value="2,140"
              icon={ViewIcon}
              trend={12.5}
            />
            <MetricCard
              title="Add to Cart"
              value="147"
              icon={CartIcon}
              trend={8.3}
            />
            <MetricCard
              title="Purchases"
              value="50"
              icon={ProductIcon}
              trend={-2.1}
            />
            <MetricCard
              title="Revenue"
              value="$4,000"
              icon={CashDollarIcon}
              trend={15.7}
            />
          </InlineGrid>
        </BlockStack>
      </Layout.Section>

      <Layout.Section>
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingLg">Product Performance</Text>
                <TextField
                  placeholder="Search products..."
                  value={searchValue}
                  onChange={setSearchValue}
                  prefix={<Icon source={SearchIcon} />}
                  autoComplete="off"
                />
              </InlineStack>

              {rows.length > 0 ? (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'numeric',
                    'numeric',
                    'numeric',
                    'numeric',
                    'numeric',
                  ]}
                  headings={[
                    'Product',
                    'Status',
                    'Views',
                    'Add to Cart',
                    'Purchases',
                    'Revenue',
                    'Conversion',
                  ]}
                  rows={rows}
                />
              ) : (
                <EmptyState
                  heading="No products found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Try adjusting your search</p>
                </EmptyState>
              )}
            </BlockStack>
          </Box>
        </Card>
      </Layout.Section>
    </Layout>
  )
}

export default ProductAnalytics